import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChatMessage, sendMessage, subscribeToMessages } from '../services/chatService';
import { useBlockedUsers, blockUser } from '../services/blockService';
import { submitReport, REPORT_REASONS, ReportReason } from '../services/reportService';
import { useAuth } from '../contexts/AuthContext';
import { isContentClean, getFilterMessage } from '../utils/contentFilter';
import { captureScreenshot, uploadEvidence } from '../services/reportEvidenceService';
import './TableChat.css';

interface TableChatProps {
  tableId: string;
  currentUserId: string;
  currentUserName: string;
  isOpen: boolean;
  onClose: () => void;
  onNewMessage?: () => void; // Callback when new message arrives (for notification)
}

const TableChat: React.FC<TableChatProps> = ({
  tableId,
  currentUserId,
  currentUserName,
  isOpen,
  onClose,
  onNewMessage,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  // Block / report from a message, and moderation mute state
  const blockedUsers = useBlockedUsers(currentUserId);
  const { userProfile } = useAuth();
  const chatMutedUntil = userProfile?.chatMutedUntil && userProfile.chatMutedUntil.getTime() > Date.now()
    ? userProfile.chatMutedUntil : null;
  const [actionFor, setActionFor] = useState<string | null>(null);
  const [reportFor, setReportFor] = useState<{ id: string; name: string; text: string } | null>(null);
  const [reportReason, setReportReason] = useState<ReportReason | ''>('');
  const [chatActionBusy, setChatActionBusy] = useState(false);
  const [chatNotice, setChatNotice] = useState<string | null>(null);
  const [evidenceBlob, setEvidenceBlob] = useState<Blob | null>(null);
  const [evidencePreview, setEvidencePreview] = useState<string | null>(null);
  const clearEvidence = () => { if (evidencePreview) URL.revokeObjectURL(evidencePreview); setEvidenceBlob(null); setEvidencePreview(null); };
  const startReport = (target: { id: string; name: string; text: string }) => {
    setActionFor(null);
    setReportFor(target);
    setReportReason('');
    // Grab the screen as it is right now, before the report box appears.
    captureScreenshot().then((blob) => {
      if (!blob) return;
      setEvidenceBlob(blob);
      setEvidencePreview(URL.createObjectURL(blob));
    });
  };

  const handleBlockFromChat = async (senderId: string, senderName: string) => {
    setChatActionBusy(true);
    const r = await blockUser(senderId);
    setChatActionBusy(false);
    setActionFor(null);
    setChatNotice(r.success ? `${senderName} blocked. Their messages are hidden for you.` : (r.error || 'Could not block.'));
    setTimeout(() => setChatNotice(null), 3000);
  };
  const handleSubmitChatReport = async () => {
    if (!reportFor || !reportReason) return;
    setChatActionBusy(true);
    let evidenceUrls: string[] = [];
    if (evidenceBlob) {
      try { evidenceUrls = [await uploadEvidence(currentUserId, evidenceBlob)]; }
      catch (e) { console.warn('[evidence] upload failed, sending report without it', e); }
    }
    const r = await submitReport(reportFor.id, reportReason, `Table chat at ${tableId}: "${reportFor.text.slice(0, 200)}"`, evidenceUrls);
    setChatActionBusy(false);
    setReportFor(null);
    setReportReason('');
    clearEvidence();
    setChatNotice(r.success ? 'Report sent. Thank you.' : (r.message || 'Could not send report.'));
    setTimeout(() => setChatNotice(null), 3000);
  };
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastMessageCountRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Subscribe to messages
  useEffect(() => {
    if (!tableId) return;

    const unsubscribe = subscribeToMessages(tableId, (newMessages) => {
      setMessages(newMessages);
      
      // Check if there's a new message (not from current user)
      if (newMessages.length > lastMessageCountRef.current) {
        const latestMessage = newMessages[newMessages.length - 1];
        if (latestMessage && latestMessage.senderId !== currentUserId && onNewMessage) {
          onNewMessage();
        }
      }
      lastMessageCountRef.current = newMessages.length;
    });

    return () => unsubscribe();
  }, [tableId, currentUserId, onNewMessage]);

  // Scroll to bottom when messages change or chat opens
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // iOS keyboard handling: with the new full-height right-side panel the
  // input already sits at the bottom and stays visible above the keyboard
  // (visual viewport shrinks under it). Just scroll the input into view
  // on focus - no need to reposition the whole container.
  useEffect(() => {
    if (!isOpen) return;
    const input = inputRef.current;
    if (!input) return;
    const handleFocus = () => {
      setTimeout(() => {
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    };
    input.addEventListener('focus', handleFocus);
    return () => {
      input.removeEventListener('focus', handleFocus);
    };
  }, [isOpen]);

  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim() || isSending) return;

    const messageText = newMessage.trim();
    // Client-side gate: warn and keep the message on the device. The server
    // runs the same check on anything that gets past this and hands out
    // strikes, so this is the friendly layer, not the only one.
    if (!isContentClean(messageText)) {
      setChatNotice(getFilterMessage('chat'));
      setTimeout(() => setChatNotice(null), 4000);
      return;
    }
    setNewMessage('');
    setIsSending(true);

    try {
      await sendMessage(tableId, currentUserId, currentUserName, messageText);
    } catch (error) {
      console.error('Failed to send message:', error);
      setNewMessage(messageText); // Restore message on error
    } finally {
      setIsSending(false);
    }
  }, [newMessage, tableId, currentUserId, currentUserName, isSending]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Quick-reply chip phrases - table-friendly canned messages that don't
  // need typing. Tap a chip to send it immediately. Easy to extend.
  const QUICK_REPLIES = [
    'gg',
    'nh',
    'nice hand',
    'unlucky',
    'ship it',
    'tough spot',
    "let's go!",
    'wp',
  ];

  const sendQuickReply = useCallback(async (text: string) => {
    if (isSending) return;
    setIsSending(true);
    try {
      await sendMessage(tableId, currentUserId, currentUserName, text);
    } catch (error) {
      console.error('Failed to send quick reply:', error);
    } finally {
      setIsSending(false);
    }
  }, [tableId, currentUserId, currentUserName, isSending]);

  if (!isOpen) return null;

  return (
    <div className="table-chat-overlay">
      <div className="table-chat-container" ref={containerRef}>
        <div className="chat-header">
          <span className="chat-title">Table Chat</span>
          <button className="chat-close-btn" onClick={onClose} aria-label="Close chat">×</button>
        </div>

        <div className="chat-messages">
          {messages.length === 0 ? (
            <div className="chat-empty">
              <span>No messages yet</span>
              <span className="chat-empty-sub">Be the first to say something!</span>
            </div>
          ) : (
            messages.filter((msg) => !(msg.senderId && blockedUsers.has(msg.senderId))).map((msg) => {
              const isBot = msg.senderId?.startsWith('house_bot_') || (msg as any).isHouseBot;
              return (
                <div
                  key={msg.id}
                  className={`chat-message ${
                    msg.type === 'system'
                      ? 'system-message'
                      : isBot
                        ? 'bot-message'
                        : msg.senderId === currentUserId
                          ? 'own-message'
                          : 'other-message'
                  }`}
                >
                  {msg.type === 'system' ? (
                    <div className="system-message-content">
                      <span className="system-icon">ℹ</span>

                      <span>{msg.message}</span>
                    </div>
                  ) : (
                    <>
                      <div className="message-header">
                        <span
                          className={`sender-name ${!isBot && msg.senderId !== currentUserId ? 'tappable' : ''}`}
                          style={isBot ? { color: '#f59e0b' } : undefined}
                          onClick={() => { if (!isBot && msg.senderId !== currentUserId) setActionFor(actionFor === msg.id ? null : msg.id); }}
                          title={!isBot && msg.senderId !== currentUserId ? 'Block or report' : undefined}
                        >
                          {isBot ? ` ${msg.senderName}` : msg.senderId === currentUserId ? 'You' : msg.senderName}
                        </span>
                        <span className="message-time">{formatTime(msg.timestamp)}</span>
                      </div>
                      <div className={`message-content ${(msg as any).moderated ? 'moderated' : ''}`}>{msg.message}</div>
                      {actionFor === msg.id && (
                        <div className="chat-msg-actions">
                          <button disabled={chatActionBusy} onClick={() => handleBlockFromChat(msg.senderId, msg.senderName)}>Block</button>
                          <button onClick={() => startReport({ id: msg.senderId, name: msg.senderName, text: msg.message })}>Report</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick-reply chips - one-tap canned messages */}
        {chatNotice && <div className="chat-notice">{chatNotice}</div>}
        {reportFor && (
          <div className="chat-report-box">
            <div className="chat-report-title">Report {reportFor.name}</div>
            <div className="chat-report-reasons">
              {REPORT_REASONS.map((r) => (
                <button
                  key={r.value}
                  className={`chat-report-reason ${reportReason === r.value ? 'active' : ''}`}
                  onClick={() => setReportReason(r.value)}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {evidencePreview && (
              <div className="chat-report-evidence">
                <img src={evidencePreview} alt="Screenshot attached" />
                <span>Screenshot attached</span>
                <button onClick={clearEvidence}>Remove</button>
              </div>
            )}
            <div className="chat-report-actions">
              <button className="chat-report-submit" disabled={!reportReason || chatActionBusy} onClick={handleSubmitChatReport}>Submit</button>
              <button className="chat-report-cancel" onClick={() => { setReportFor(null); clearEvidence(); }}>Cancel</button>
            </div>
          </div>
        )}
        <div className="chat-quick-replies">
          {QUICK_REPLIES.map((text) => (
            <button
              key={text}
              className="chat-quick-chip"
              onClick={() => sendQuickReply(text)}
              disabled={isSending || !!chatMutedUntil}
            >
              {text}
            </button>
          ))}
        </div>

        <div className="chat-input-container">
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={chatMutedUntil ? `Chat muted until ${chatMutedUntil.toLocaleString()}` : 'Type a message...'}
            className="chat-input"
            maxLength={200}
            disabled={isSending || !!chatMutedUntil}
          />
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || isSending || !!chatMutedUntil}
            className="chat-send-btn"
            aria-label="Send"
          >
            {isSending ? '...' : ''}

          </button>
        </div>
      </div>
    </div>
  );
};

export default TableChat;